import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as membershipApi from '@/features/membership/api';
import type {
  InviteOrganizationAdminInput,
  InviteStudentInput,
  InviteTeacherInput,
  InviteToOrganizationResult,
  OrganizationInvitationView,
} from '@/features/membership/types';
import { useActiveOrganization } from '@/features/organization/active-organization-context';
import type { ApiError } from '@/lib/api/client';
import { TENANT_QUERY_PREFIX } from '@/lib/api/tenant-query';

/**
 * Tenant-scoped query key embeds the Active Organization id (ADR-026
 * Addendum, AO-9) - Org A's cached invitation list cannot structurally
 * satisfy an Org B query, independent of the broader tenant cache clear
 * `ActiveOrganizationProvider` also performs on switch/logout.
 */
const invitationsKey = (organizationId: string | null): readonly unknown[] => [
  TENANT_QUERY_PREFIX,
  organizationId,
  'organization-invitations',
];

export function useOrganizationInvitations(): UseQueryResult<
  readonly OrganizationInvitationView[],
  ApiError
> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: invitationsKey(activeOrganizationId),
    queryFn: () => membershipApi.listOrganizationInvitations(),
    enabled: activeOrganizationId !== null,
  });
}

export function useInviteTeacher(): UseMutationResult<
  InviteToOrganizationResult,
  ApiError,
  InviteTeacherInput
> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (input: InviteTeacherInput) => membershipApi.inviteTeacher(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invitationsKey(activeOrganizationId) });
    },
  });
}

export function useInviteStudent(): UseMutationResult<
  InviteToOrganizationResult,
  ApiError,
  InviteStudentInput
> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (input: InviteStudentInput) => membershipApi.inviteStudent(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invitationsKey(activeOrganizationId) });
    },
  });
}

/**
 * Platform-scoped (ADR-027, Decision 6) - deliberately does not read or
 * invalidate any tenant-scoped query key, since the caller (a Platform
 * Super Admin) holds no Active Organization of their own to key one by.
 */
export function useInviteOrganizationAdmin(): UseMutationResult<
  InviteToOrganizationResult,
  ApiError,
  InviteOrganizationAdminInput
> {
  return useMutation({
    mutationFn: (input: InviteOrganizationAdminInput) =>
      membershipApi.inviteOrganizationAdmin(input),
  });
}

export function useResendOrganizationInvitation(): UseMutationResult<
  InviteToOrganizationResult,
  ApiError,
  string
> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (id: string) => membershipApi.resendOrganizationInvitation(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invitationsKey(activeOrganizationId) });
    },
  });
}

export function useCancelOrganizationInvitation(): UseMutationResult<
  OrganizationInvitationView,
  ApiError,
  string
> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (id: string) => membershipApi.cancelOrganizationInvitation(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invitationsKey(activeOrganizationId) });
    },
  });
}

/**
 * Not tenant-scoped (ADR-027, Decision 13) - acceptance works with no
 * Active Organization selected, so this mutation does not read one. On
 * success it invalidates every organization's cached tenant-scoped data,
 * since the recipient's membership set has just changed and AO-13
 * re-resolves it truthfully via `/auth/me` rather than assuming which
 * organization was just granted (mirrors `useAcceptGuardianInvitation`).
 */
export function useAcceptOrganizationInvitation(): UseMutationResult<
  OrganizationInvitationView,
  ApiError,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => membershipApi.acceptOrganizationInvitation(token),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [TENANT_QUERY_PREFIX] });
    },
  });
}
